import { NetClient } from '../net/NetClient';
import { getTurnstileToken } from '../net/turnstile';
import { isValidCode, normalizeCode } from '../../shared/protocol';
import { loadName, saveName } from '../session';
import { BUILD_TAG } from '../buildTag';

export interface MenuCallbacks {
  onSingle(): void;
  onHost(serverUrl: string, code: string, name: string): void;
  onJoin(serverUrl: string, code: string, name: string): void;
}

const BTN =
  'display:block;width:100%;margin:6px 0;padding:11px 14px;font-size:16px;font-weight:700;' +
  'color:#0a0a0f;background:#5dffd4;border:none;border-radius:10px;cursor:pointer;';
const BTN_ALT =
  'display:block;width:100%;margin:6px 0;padding:11px 14px;font-size:16px;font-weight:700;' +
  'color:#5dffd4;background:transparent;border:2px solid #5dffd4;border-radius:10px;cursor:pointer;';
const BTN_GHOST =
  'display:block;width:100%;margin:6px 0;padding:8px 14px;font-size:13px;' +
  'color:#e8e8ef;background:transparent;border:1px solid rgba(232,232,239,0.35);border-radius:10px;cursor:pointer;';
const INPUT =
  'display:block;width:100%;margin:6px 0;padding:10px 12px;font-size:15px;text-align:center;' +
  'color:#e8e8ef;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.25);border-radius:10px;';

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error('waktu habis')), ms);
    promise.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(t);
        reject(e);
      },
    );
  });
}

/** Menu utama: nama + Single/Multiplayer + buat/gabung room berkode. */
export class MainMenu {
  private root: HTMLDivElement;
  private nameInput: HTMLInputElement;
  private multiBox: HTMLDivElement;
  private codeInput: HTMLInputElement;
  private statusEl: HTMLDivElement;
  private busy = false;

  constructor(
    private panel: HTMLElement,
    private serverUrl: string,
    private cb: MenuCallbacks,
  ) {
    const saved = loadName();
    this.root = document.createElement('div');
    this.root.style.cssText = 'width:min(360px,92vw);';

    const title = document.createElement('h1');
    title.style.cssText = 'font-size:30px;letter-spacing:3px;margin-bottom:2px;';
    title.textContent = 'SLIME BRAWLER';
    this.root.appendChild(title);
    const tag = document.createElement('div');
    tag.style.cssText = 'font-size:11px;opacity:0.5;margin-bottom:12px;';
    tag.textContent = `v${BUILD_TAG}`;
    this.root.appendChild(tag);

    const nameLabel = document.createElement('div');
    nameLabel.style.cssText = 'font-size:13px;opacity:0.8;text-align:left;';
    nameLabel.textContent = 'Namamu:';
    this.root.appendChild(nameLabel);
    this.nameInput = document.createElement('input');
    this.nameInput.maxLength = 16;
    this.nameInput.placeholder = 'contoh: Budi';
    this.nameInput.value = saved;
    this.nameInput.style.cssText = INPUT;
    this.root.appendChild(this.nameInput);

    const single = document.createElement('button');
    single.style.cssText = BTN;
    single.textContent = 'MAIN SENDIRI';
    single.onclick = () => this.cb.onSingle();
    this.root.appendChild(single);

    const multi = document.createElement('button');
    multi.style.cssText = BTN_ALT;
    multi.textContent = 'MAIN BARENG (ONLINE)';
    multi.onclick = () => this.toggleMulti();
    this.root.appendChild(multi);

    this.multiBox = document.createElement('div');
    this.multiBox.style.cssText = 'display:none;margin-top:4px;';
    this.root.appendChild(this.multiBox);

    const host = document.createElement('button');
    host.style.cssText = BTN;
    host.textContent = 'BUAT ROOM BARU';
    host.onclick = () => void this.createRoom();
    this.multiBox.appendChild(host);

    const joinRow = document.createElement('div');
    joinRow.style.cssText = 'display:flex;gap:8px;';
    this.codeInput = document.createElement('input');
    this.codeInput.maxLength = 6;
    this.codeInput.placeholder = 'KODE';
    this.codeInput.style.cssText = INPUT + 'text-transform:uppercase;letter-spacing:4px;flex:1;margin:6px 0;';
    joinRow.appendChild(this.codeInput);
    const join = document.createElement('button');
    join.style.cssText = BTN_ALT + 'flex:1;margin:6px 0;';
    join.textContent = 'GABUNG';
    join.onclick = () => this.joinRoom();
    joinRow.appendChild(join);
    this.multiBox.appendChild(joinRow);

    const back = document.createElement('button');
    back.style.cssText = BTN_GHOST;
    back.textContent = 'Tutup';
    back.onclick = () => this.toggleMulti(false);
    this.multiBox.appendChild(back);

    this.statusEl = document.createElement('div');
    this.statusEl.style.cssText = 'font-size:13px;min-height:20px;color:#ffb3ab;margin-top:4px;';
    this.root.appendChild(this.statusEl);

    const help = document.createElement('div');
    help.style.cssText = 'font-size:12px;opacity:0.6;margin-top:10px;line-height:1.7;';
    help.innerHTML =
      'WASD gerak &middot; Shift lari &middot; Space lompat<br>Dekati crate lalu tahan klik kiri untuk tarik/dorong';
    this.root.appendChild(help);

    this.panel.appendChild(this.root);
    if (!serverUrl) {
      this.statusEl.textContent = 'Server multiplayer belum dikonfigurasi di build ini.';
    }
  }

  private playerName(): string {
    const name = this.nameInput.value.trim().slice(0, 16) || 'Slime';
    saveName(this.nameInput.value);
    return name;
  }

  private toggleMulti(force?: boolean): void {
    const show = force ?? this.multiBox.style.display === 'none';
    this.multiBox.style.display = show ? 'block' : 'none';
    if (show && !this.serverUrl) {
      this.statusEl.textContent = 'Server multiplayer belum dikonfigurasi di build ini.';
    } else {
      this.statusEl.textContent = '';
    }
  }

  private async createRoom(): Promise<void> {
    if (this.busy || !this.serverUrl) return;
    this.busy = true;
    this.statusEl.style.color = '#e8e8ef';
    this.statusEl.textContent = 'Membuat room…';
    try {
      const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined ?? '').trim();
      let token = '';
      try {
        token = await withTimeout(getTurnstileToken(siteKey), 8000);
      } catch (err) {
        const msg = err instanceof Error ? err.message : '';
        this.statusEl.style.color = '#ffb3ab';
        this.statusEl.textContent = `Verifikasi gagal/telat: ${msg}`;
        return;
      }
      if (token === '' && siteKey) {
        this.statusEl.style.color = '#ffb3ab';
        this.statusEl.textContent = 'Verifikasi anti-bot gagal. Coba lagi.';
        return;
      }
      const code = await withTimeout(NetClient.createRoom(this.serverUrl, token), 10000);
      this.statusEl.textContent = '';
      this.cb.onHost(this.serverUrl, code, this.playerName());
    } catch {
      this.statusEl.style.color = '#ffb3ab';
      this.statusEl.textContent = 'Gagal membuat room (waktu habis/terputus). Coba lagi.';
    } finally {
      this.busy = false;
    }
  }

  private joinRoom(): void {
    if (this.busy || !this.serverUrl) return;
    const code = normalizeCode(this.codeInput.value.trim());
    if (!isValidCode(code)) {
      this.statusEl.style.color = '#ffb3ab';
      this.statusEl.textContent = 'Kode harus 6 karakter (A-Z, 2-9).';
      return;
    }
    // Verifikasi anti-bot dilakukan di Lobby dengan token BARU (token
    // Turnstile single-use; mengecek di menu membuang satu token).
    this.cb.onJoin(this.serverUrl, code, this.playerName());
  }

  dispose(): void {
    this.root.remove();
  }
}
